import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { VoucherCardProps } from './voucher-card.types';
import { cn } from '../../utils/cn';
import styles from './voucher-card.module.css';
import Divider from '../../atoms/divider';
import Text from '../../atoms/text';
import Button from '../../atoms/button';
import { DEFAULT_COUPON_IMAGE } from '../../molecules/my-coupon-card/my-coupon-card.constants';
import { useFormatDate } from '../../utils/use-format-date';
import Image from '../../atoms/image';

export const VoucherCard: React.FC<VoucherCardProps> = ({
    isAdding = false,
    voucher,
    handleVoucher,
    status = false,
    labelButton = 'Áp dụng',
    textExpiry = 'HSD',
    className,
    size = 'large',
}) => {
    const { background_color, border_color, name, condition, instruction, salesrule, imageUri } = voucher;
    const { formatDate } = useFormatDate();

    const validityPeriod = salesrule
        ? `${formatDate(salesrule.from_date, 'short')} - ${formatDate(salesrule.to_date, 'full')}`
        : '';

    const rootStyle = {
        '--voucher-bg': background_color || 'var(--color-tertiary-10)',
        '--voucher-border': border_color || 'var(--color-tertiary-70)',
        '--voucher-button-bg': status || isAdding ? 'var(--color-neutral-30)' : 'var(--color-accent-orange)',
    } as React.CSSProperties;

    const [imageSrc, setImageSrc] = useState(imageUri || DEFAULT_COUPON_IMAGE);

    useEffect(() => {
        setImageSrc(imageUri || DEFAULT_COUPON_IMAGE);
    }, [imageUri]);

    const handleImageError = () => {
        if (imageSrc !== DEFAULT_COUPON_IMAGE) {
            setImageSrc(DEFAULT_COUPON_IMAGE);
        }
    };

    const imageSize = size === 'small' ? { width: 70, height: 44 } : { width: 58, height: 58 };

    const sizeClassMap = {
        small: styles.small,
        medium: styles.large,
        large: styles.large,
    };

    const sizeClass = sizeClassMap[size];

    const rootClassName = useMemo(() => {
        return cn(styles.root, sizeClass, className);
    }, [sizeClass, className]);

    const buttonClassName = useMemo(() => {
        return cn(styles.button, isAdding && styles.loadingButton);
    }, [isAdding]);

    const handleVoucherClick = useCallback(() => {
        handleVoucher?.(voucher);
    }, [handleVoucher, voucher]);
    return (
        <div className={rootClassName} style={rootStyle}>
            <div className={styles.content}>
                <div className={styles.leftOuter}>
                    <div className={styles.leftInner}>
                        <div className={styles.imageSection}>
                            <div className={styles.imageRotate}>
                                <Image
                                    src={imageSrc}
                                    objectFit="contain"
                                    alt={name || 'Voucher'}
                                    className={styles.image}
                                    width={imageSize.width}
                                    height={imageSize.height}
                                    onError={handleImageError}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.dividerWrap}>
                    <Divider className={styles.divider} orientation="vertical" thickness={2} />
                </div>

                <div className={styles.rightOuter}>
                    <div className={styles.rightInner}>
                        <div className={styles.detailContent}>
                            <div className={styles.topContent}>
                                <Text showIf={!!name} className={styles.title}>
                                    {name}
                                </Text>

                                <Text
                                    showIf={!!condition}
                                    className={styles.condition}
                                    dangerouslySetInnerHTML={{ __html: condition || '' }}
                                />

                                <Text
                                    showIf={!!instruction}
                                    className={styles.instruction}
                                    dangerouslySetInnerHTML={{ __html: instruction || '' }}
                                />
                            </div>

                            <div className={styles.bottomContent}>
                                <Text className={styles.expiry}>
                                    {textExpiry}: {validityPeriod}
                                </Text>

                                <Button
                                    loading={isAdding}
                                    showIf={!!handleVoucher}
                                    onClick={handleVoucherClick}
                                    disabled={status || isAdding}
                                    data-testid="voucher-button"
                                    className={buttonClassName}
                                >
                                    {labelButton}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
